import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, X } from 'lucide-react';
import { useBox } from '../context/BoxContext';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

export const BoxVisualizer = ({ onCheckout }) => {
  const { 
    selectedProducts, 
    boxSize, 
    boxSizes,
    changeBoxSize, 
    removeProduct, 
    isFull, 
    progress,
    getCurrentBoxInfo,
    loading
  } = useBox();

  const boxInfo = getCurrentBoxInfo();
  const slots = Array.from({ length: boxSize || 3 }, (_, i) => selectedProducts[i] || null);

  if (loading || !boxSize) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-lg" data-testid="box-visualizer">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="aspect-square bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-lg" data-testid="box-visualizer">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#0FA49C]/10 flex items-center justify-center">
            <Package className="w-6 h-6 text-[#0FA49C]" />
          </div>
          <div>
            <h2 className="font-bold text-[#222] text-xl">{boxInfo.name}</h2>
            <p className="text-[#666] text-sm">{boxSize} samples • £{boxInfo.price.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-[#666]">
            {selectedProducts.length} of {boxSize} selected
          </span>
          <span className="font-semibold text-[#0FA49C]">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Box Grid */}
      <div 
        className={`grid gap-3 mb-4 ${
          boxSize <= 4 ? 'grid-cols-2' : boxSize <= 6 ? 'grid-cols-3' : 'grid-cols-5'
        }`}
        data-testid="box-slots"
      >
        <AnimatePresence mode="popLayout">
          {slots.map((product, index) => (
            <motion.div
              key={product ? product.id : `empty-${index}`}
              layout
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className={`box-slot aspect-square rounded-xl border-2 border-dashed flex items-center justify-center relative overflow-hidden ${
                product 
                  ? 'filled border-solid border-[#0FA49C] bg-[#0FA49C]/5' 
                  : 'border-gray-200 bg-gray-50'
              }`}
              data-testid={`box-slot-${index}`}
            >
              {product ? (
                <>
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => removeProduct(product.id)}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    data-testid={`remove-product-${product.id}`}
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                    <p className="text-white text-[10px] font-medium truncate">{product.name}</p>
                  </div>
                </>
              ) : (
                <span className="text-gray-300 text-xs font-medium">+</span>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Box Size Selector */}
      <div className="mb-3">
        <p className="text-xs sm:text-sm font-medium text-[#666] mb-2">Change Box Size</p>
        <div className="flex gap-2">
          {boxSizes.map((size) => (
            <button
              key={size.size}
              onClick={() => changeBoxSize(size.size)}
              className={`flex-1 py-1.5 sm:py-2 px-2 sm:px-3 rounded-xl text-xs sm:text-sm font-semibold border transition-colors ${
                boxSize === size.size
                  ? 'bg-[#0FA49C] text-white border-[#0FA49C]'
                  : 'bg-white text-[#222] border-gray-200 hover:border-[#0FA49C]/50'
              }`}
              data-testid={`box-size-${size.size}`}
            >
              {size.size} • £{size.price.toFixed(2)}
            </button>
          ))}
        </div>
      </div>

      {/* Checkout Button */}
      <Button
        className="w-full bg-[#0FA49C] text-white hover:bg-[#0d8f88] rounded-xl py-6 text-lg font-bold btn-primary"
        disabled={!isFull}
        onClick={onCheckout}
        data-testid="checkout-btn"
      >
        {isFull ? (
          <>Complete Your Box • £{boxInfo.price.toFixed(2)}</>
        ) : (
          <>Select {boxSize - selectedProducts.length} more sample{boxSize - selectedProducts.length !== 1 ? 's' : ''}</>
        )}
      </Button>
    </div>
  );
};
